import { fromJS, Record, List } from 'immutable';
import _ from 'lodash';

import trainTime from '../lib/TrainTime';

import BaseStore from './BaseStore';
import { ActionTypes, SmartCarnetValidation } from '../constants/AppConstants';

import SmartCarnetRecordFilters from '../records/smartCarnet/SmartCarnetRecordFilters';

const MAX_SMART_CARNET_BOOKS = 6;

export const MAX_SMART_CARNET_TOTAL_PASSES =
    MAX_SMART_CARNET_BOOKS * SmartCarnetValidation.PASSES_PER_BOOK;

/*
    * Returns the minimum price of all fares.
    * If no fares are available, returns null.
    * @param {List} fares - List of fare objects with a totalPrice property.
    * @returns {number|null} - Minimum total price or null if no fares are available.

    Unit tests have discovered that this function does not actually work correctly
*/
export const getMinReturnFaresPrice = fares => {
    if (fares.size > 0) {
        return _.min(fares.map(fare => fare.totalPrice).toJS());
    }

    return null;
};

export const groupTicketsIntoJourneys = tickets => {
    const journeys = {};

    tickets.forEach(ticket => {
        const destIndex = `${ticket.originCode}-${
            ticket.destinationCode
        }-${ticket.description.trim()}`;
        if (typeof journeys[destIndex] === 'undefined') {
            journeys[destIndex] = {
                id: destIndex,
                description: ticket.description,
                isOffPeak: ticket.description.includes('OFF'),
                originCode: ticket.originCode,
                originName: ticket.originName,
                destinationCode: ticket.destinationCode,
                destinationName: ticket.destinationName,
                tickets: [],
            };
        }

        journeys[destIndex].tickets.push(
            ticket.set(
                'isExpired',
                ticket
                    .get('validityEndDate')
                    .add(1, 'day')
                    .isBefore(trainTime()),
            ),
        );
    });

    const result = Object.entries(journeys).map(value => {
        const journey = value[1];
        const sortedTickets = fromJS(journey.tickets).sortBy(ticket => ticket.validityStartDate);

        return {
            ...journey,
            remainingPassCount: journey.tickets.reduce(
                (acc, ticket) => (acc += ticket.remainingPassCount),
                0,
            ),
            tickets: sortedTickets,
        };
    });

    return result;
};

const SmartCarnetState = new Record({
    journeys: new List(),
    limitError: null,
    loading: true,
    loadingPasses: null,
    loadingTickets: null,
    fares: new List(),
    filters: new SmartCarnetRecordFilters(),
    returnFares: new List(),
    returnLoading: true,
    returnLimitError: null,
    ticketCode: null,
    tickets: new List(),
    ticketsError: null,
});

export class SmartCarnetStore extends BaseStore {
    getInitialState() {
        return new SmartCarnetState();
    }

    getFares() {
        return this.state.fares;
    }

    getJourneys() {
        return this.state.journeys;
    }

    getReturnFares() {
        return this.state.returnFares;
    }

    getReturnLoading() {
        return this.state.returnLoading;
    }

    getFilters() {
        return this.state.filters;
    }

    getLimitError() {
        return this.state.limitError;
    }

    getLoading() {
        return this.state.loading;
    }

    getLoadingPasses() {
        return this.state.loadingPasses;
    }

    hasActivePasses() {
        return this.state.journeys.some(journeys => journeys.get("remainingPassCount") > 0);
    }

    getLoadingTickets() {
        return this.state.loadingTickets;
    }

    getReturnLimitError() {
        return this.state.returnLimitError;
    }

    getTicketCode() {
        return this.state.ticketCode;
    }

    getTickets() {
        return this.state.tickets;
    }

    /* Search */
    handleSearchStarted() {
        this.setState(this.state.set('loading', true));
    }

    handleFareSearchFailed() {
        this.setState(
            this.state.merge({
                loading: false,
                fares: new List() // reset the fares
            }),
        );
    }

    handleFareSearchFinished({ fares }) {
        this.setState(
            this.state.merge({
                loading: false,
                fares,
            }),
        );
    }

    /* Return search */
    handleReturnSearchStarted() {
        this.setState(this.state.set('returnLoading', true));
    }

    handleReturnSearchFailed() {
        this.setState(this.state.set('returnLoading', false));
    }

    handleReturnSearchFinished({ fares }) {
        this.setState(
            this.state.merge({
                returnLoading: false,
                returnFares: fares,
            }),
        );
    }

    handleReturnClosed() {
        this.setState(
            this.state.merge({
                returnLoading: false,
                returnLimitError: false,
            }),
        );
    }

    /* Filters */
    handleFilterChanged({ filterType, value }) {
        this.setState(
            this.state.set('filters', this.state.filters.updateFilter(filterType, value)),
        );
    }

    handlePassesStarted({ ticketCode, isReturn = false }) {
        this.setState(
            this.state.merge({
                [isReturn ? 'returnLimitError' : 'limitError']: null,
                loadingPasses: true,
                ticketCode,
            }),
        );
    }

    handlePassesFailed({ limitError = null, isReturn = false }) {
        this.setState(
            this.state.merge({
                loadingPasses: false,
                ticketCode: null,
                [isReturn ? 'returnLimitError' : 'limitError']: limitError,
            }),
        );
    }

    handlePassesFinished() {
        this.setState(
            this.state.merge({
                loadingPasses: false,
                ticketCode: null,
            }),
        );
    }

    handleTicketsStarted() {
        this.setState(
            this.state.merge({
                loadingTickets: true,
                ticketsError: null,
            }),
        );
    }

    handleTicketsFailed({ error = null }) {
        this.setState(
            this.state.merge({
                loadingTickets: false,
                ticketsError: error,
            }),
        );
    }

    handleTicketsFinished({ tickets }) {
        this.setState(
            this.state.merge({
                journeys: groupTicketsIntoJourneys(tickets),
                loadingTickets: false,
                tickets,
            }),
        );
    }

    getActionHandlers() {
        return {
            [ActionTypes.SMART_CARNET_SEARCH_STARTED]: this.handleSearchStarted,
            [ActionTypes.SMART_CARNET_SEARCH_FAILED]: this.handleFareSearchFailed,
            [ActionTypes.SMART_CARNET_SEARCH_FINISHED]: this.handleFareSearchFinished,
            [ActionTypes.SMART_CARNET_RETURN_SEARCH_STARTED]: this.handleReturnSearchStarted,
            [ActionTypes.SMART_CARNET_RETURN_SEARCH_FAILED]: this.handleReturnSearchFailed,
            [ActionTypes.SMART_CARNET_RETURN_SEARCH_FINISHED]: this.handleReturnSearchFinished,
            [ActionTypes.SMART_CARNET_RETURN_CLOSED]: this.handleReturnClosed,
            [ActionTypes.SMART_CARNET_FILTER_CHANGED]: this.handleFilterChanged,
            [ActionTypes.SMART_CARNET_PASSES_STARTED]: this.handlePassesStarted,
            [ActionTypes.SMART_CARNET_PASSES_FAILED]: this.handlePassesFailed,
            [ActionTypes.SMART_CARNET_PASSES_FINISHED]: this.handlePassesFinished,
            [ActionTypes.SMART_CARNET_TICKETS_STARTED]: this.handleTicketsStarted,
            [ActionTypes.SMART_CARNET_TICKETS_FAILED]: this.handleTicketsFailed,
            [ActionTypes.SMART_CARNET_TICKETS_FINISHED]: this.handleTicketsFinished,
        };
    }
}

const storeInstance = new SmartCarnetStore();

window.carnetState = storeInstance;

export default storeInstance;



// WEBPACK FOOTER //
// ./src/stores/SmartCarnetStore.js